<template>
  <section-pdf-viewer :section-doc="section"
                      dom-ref="CONTRAT_VIEWER"
                      :dialog="dialog"
                      :extended="extended"
                      :maximise="!dialog"
                      :mode="mode"
                      :show-modif="showModif"
                      :sub-section="subSection"
                      :fullscreen="dialog"
                      :read-only="readOnly"
                      :show-chat="showChat"
                      @extended="setExtended"
                      @close-dialog="closeDialog"
                      @close-sub-dialog="closeSubDialog">
    <template v-if="!showModif" #footer-status>
      <div class="contract-signature-status">
        <p v-if="signatureMessage && !(isEmployeur && signClicked && !signatureComplete)" class="mb-1">{{ signatureMessage }}</p>
        <template v-if="signClicked && !signatureComplete">
          <p class="mb-1">Le contrat est en cours de signature ou en attente du retour d’Edusign.</p>
          <p v-if="popupBlocked" class="mb-1">
            Votre navigateur a bloqué la fenêtre de signature. Autorisez les popups pour ce site, puis
            <button type="button" class="text-decoration-underline" @click="signContrat(signUrl)">ouvrir la fenêtre de signature</button>.
          </p>
        </template>
        <p v-if="signError && !signatureComplete" class="mb-1">{{ signError }}</p>
      </div>
    </template>
  </section-pdf-viewer>
</template>

<script lang="ts">
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { storeChat, storeDocument, storeMetaAttrib, storeSession, storeContrat, storeContact } from '@/generated/stores';
  import SectionPdfViewer from '@/framework/document/components/SectionPdfViewer.vue';
  import Button from '@/framework/base/classes/Button';
  import Contrat from '@/extend/classes/Contrat';
  import { ContratStatuts } from '@/generated/classes/Contrat';
  import Creneau from '@/extend/classes/Creneau';
  import Document from '@/extend/classes/Document';
  import { DocumentModes, ObjType } from '@/generated/classes/Document';
  import ContratArticle from '@/extend/classes/ContratArticle';
  import Section from '@/framework/base/classes/Section';
  import SectionEditArticles from '@/custom/sections/SectionEditArticles.vue';
  import SectionRemuneration from '@/custom/sections/SectionRemuneration.vue';
  import CreneauxEditor from '@/custom/sections/CreneauxEditor.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import {
    AppButtons,
    AppSections,
    FwkButtons,
    RessourceAppButton,
    RessourceAppSection,
    RessourceFwkButton
  } from '@/ressources/globalRessources';
  import { defineComponent, type PropType } from "vue";
  import SectionValuateMetaAttrib from "@/framework/metaAttrib/components/SectionValuateMetaAttrib.vue";
  import MetaAttrib from "@/generated/classes/MetaAttrib.ts";
  import Files, { DownloadData } from "@/generated/classes/Files.ts";
  import { getBlob } from "@/framework/files/helpers/FileTypes.ts";
  import HubServices from '@/generated/hub/HubServices';
  import EduSign from "@/generated/classes/EduSign.ts";

  export default defineComponent({
    components: {
      SectionPdfViewer,
      SectionRemuneration
    },
    props: {
      contrat: {
        type: Object as PropType<Contrat>,
        required: true
      },
      dialog: {
        type: Boolean
      },
      extended: {
        type: Boolean
      },
      readOnly: {
        type: Boolean
      }
    },
    data() {
      const subSection: Section = new Section();
      return {
        subSection,
        mode: DocumentModes.NORMAL,
        showModif: false,
        showChat: false,
        mDocument: new Document(),
        buttonLoading: {} as Record<string, { loading: boolean }>,
        signing: false,
        signWindow: null as Window | null,
        lastOpenAt: 0,
        signClicked: false,
        signUrl: '',
        popupBlocked: false,
        signError: '',
        approving: false,
        disposed: false,
        signTimer: undefined as number | undefined
      };
    },
    created(): void {
      HubServices.onContratSignContrat(this.signContrat);
      window.addEventListener('focus', this.signatureReturn);
    },
    beforeUnmount(): void {
      this.disposed = true;
      window.removeEventListener('focus', this.signatureReturn);
      window.clearInterval(this.signTimer);
    },
    watch: {
      'lastContrat.statut'(statut: ContratStatuts): void {
        if (this.approving && !this.buttonLoading['sign']?.loading && this.isSalarie && statut === ContratStatuts.APPROUVED) {
          this.approving = false;
          void this.getSignLink();
        }
      }
    },
    computed: {
      lastContrat() {
        return storeContrat.getContrat(this.contrat.id);
      },
      ressource() {
        return RessourceAppSection(AppSections.CONTRAT_VIEWER);
      },
      isSalarie(): boolean {
        let res = false;
        const user = storeSession.connectedUser
        if (user && user.id > 0) {
          res = user.id == this.lastContrat.salarieId;
        }
        return res;
      },
      isEmployeur(): boolean {
        let res = false;
        const user = storeSession.connectedUser
        if (user && user.id > 0 && user.clientId > 0) {
          res = user.clientId == this.lastContrat.clientId;
        }
        return res;
      },
      signatureComplete(): boolean {
        return this.lastContrat.statut === ContratStatuts.SIGNED ||
          (this.isSalarie && this.lastContrat.statut === ContratStatuts.SALARIE_SIGNED);
      },
      signatureMessage(): string {
        if (this.lastContrat.statut === ContratStatuts.SIGNED) return 'Le contrat a été signé par toutes les parties.';
        if (this.lastContrat.statut !== ContratStatuts.SALARIE_SIGNED) return '';
        const employee = storeContact.getContact(this.lastContrat.salarieId);
        const date = this.lastContrat.approvedAt ? new Date(this.lastContrat.approvedAt) : null;
        const when = date && !Number.isNaN(date.getTime())
          ? ' le ' + date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
            ' à ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
        return 'Le contrat a été signé par "' + employee.prenom + ' ' + employee.nom + '"' + when +
          '. La signature apparaitra lorsque toutes les parties auront signé.';
      },
      metaAttrib(): MetaAttrib[] {
        const res: MetaAttrib[] = [];
        storeMetaAttrib.listMetaAttrib.forEach((metaAttrib) => {
          const clone = MetaAttrib.fromJson(metaAttrib);
          const metaValue = this.lastContrat.metaValues.find(a => a.metaAttributId == metaAttrib.id);
          if (metaValue) {
            clone.value = metaValue.value;
          }
          clone.classeId = this.lastContrat.id;
          res.push(clone);
        })
        return this.filterMetaAttrib(res);
      },
      document: {
        get(): Document {
          this.setDocument();
          return this.mDocument;
        },
        set(value: Document) {
          this.mDocument = value;
        }
      },
      section(): Section {
        const section = new Section();
        section.objType = ObjTypes.CONTRAT;
        section.objId = this.document.id;
        section.ressource = this.ressource;
        section.picto = 'SectionPictoContrat';
        section.chat = true;
        section.data = this.lastContrat;
        section.type = 'CONTRAT';
        section.buttons = this.buttons;
        section.unreadChatCount = this.unreadChatCount;
        section.chatLabel = this.chatLabel;
        return section;
      },
      unreadChatCount(): number {
        let count = 0;
        if (this.lastContrat.chatId > 0) {
          const chat = storeChat.getChat(this.lastContrat.chatId);
          if (chat) {
            count = chat.unreadCount;
          }
        }
        return count;
      },
      chatLabel(): string {
        if (this.isEmployeur) {
          return "Chat pour discuter avec votre salarié";
        } else {
          return "Chat pour discuter avec votre employeur";
        }
      },
      buttons(): Button[] {
        const buttons: Button[] = [];
        // Vue employeur
        if (this.isEmployeur) {
          // Révison affichée
          if (this.showModif) {
            const button = new Button();
            button.show = true;
            button.label = RessourceAppButton(AppButtons.DOCUMENT).label;
            button.disabled = false;
            button.outlined = true;
            button.callback = this.showRevision;
            buttons.push(button);
            // Document affiché
          } else {
            if (this.lastContrat.statut == ContratStatuts.SUBMITTED) {
              const button = new Button();
              button.show = true;
              button.label = RessourceAppButton(AppButtons.ATTENTE_SIGNATURE_SALARIE).label;
              button.disabled = false;
              button.outlined = true;
              buttons.push(button);
            }
            if (this.lastContrat.statut == ContratStatuts.SALARIE_SIGNED) {
              if (this.lastContrat.gestionnaireId == this.lastContrat.signataireId || this.signClicked) {
                const button = new Button();
                button.show = true;
                button.callback = this.getSignLink;
                button.label = this.signClicked && !this.signing ? 'Vérifier le statut de signature' : RessourceFwkButton(FwkButtons.SIGNER_CONTRAT).label;
                button.disabled = this.signing;
                button.loading = this.buttonLoading['sign']?.loading || false;
                buttons.push(button);
              } else {
                const button = new Button();
                button.show = true;
                button.callback = this.signRepresentant;
                button.label = RessourceAppButton(AppButtons.ATTENTE_SIGNATURE_REPRESENTANT).label;
                button.disabled = false;
                button.outlined = true;
                buttons.push(button);
              }
            }

            if (this.lastContrat.statut == ContratStatuts.GENERATED ||
              this.lastContrat.statut == ContratStatuts.MODIFIED) {
              // {
              //   const button = new Button();
              //   button.show = true;
              //   button.label = RessourceAppButton(AppButtons.CONTRAT_ARTICLE).label;
              //   button.disabled = false;
              //   button.outlined = true;
              //   button.callback = this.showArticles;
              //   buttons.push(button);
              // }
              {
                const button = new Button();
                button.show = true;
                button.label = RessourceAppButton(AppButtons.CONTRAT_REMUNERATION).label;
                button.disabled = false;
                button.outlined = true;
                button.callback = this.showRemuneration;
                buttons.push(button);
              }
              {
                const button = new Button();
                button.show = true;
                button.label = RessourceAppButton(AppButtons.CONTRAT_OBJET).label;
                button.disabled = false;
                button.outlined = true;
                button.callback = this.showObjet;
                buttons.push(button);
              }
              {
                const button = new Button();
                button.show = true;
                button.label = RessourceAppButton(AppButtons.CONTRAT_PERIODES).label;
                button.disabled = false;
                button.outlined = true;
                button.callback = this.showPeriodes;
                buttons.push(button);
              }
            } else if (this.lastContrat.statut != ContratStatuts.SALARIE_SIGNED ||
              this.lastContrat.gestionnaireId != this.lastContrat.signataireId) {
              const button = new Button();
              button.show = true;
              button.label = RessourceFwkButton(FwkButtons.FERMER).label;
              button.disabled = false;
              button.callback = this.fermerContrat;
              buttons.push(button);
            }

            if (this.lastContrat.hasUserUpdate) {
              const button = new Button();
              button.show = true;
              button.label = RessourceAppButton(AppButtons.REVISION).label;
              button.disabled = false;
              button.outlined = true;
              button.callback = this.showRevision;
              buttons.push(button);
            }

            if (this.lastContrat.toSubmit) {
              const button = new Button();
              button.show = true;
              button.callback = this.validateContrat;
              button.label = 'Valider et signer';
              button.disabled = false;
              buttons.push(button);
            }
          }
          // Vue salarié
        } else if (this.isSalarie) {
          // Révison affichée
          if (this.showModif) {
            const button = new Button();
            button.show = true;
            button.label = RessourceAppButton(AppButtons.DOCUMENT).label;
            button.disabled = false;
            button.outlined = true;
            button.callback = this.showRevision;
            buttons.push(button);
            // Document affiché
          } else {
            if (this.lastContrat.hasSalarieUpdate) {
              const button = new Button();
              button.show = true;
              button.label = RessourceAppButton(AppButtons.REVISION).label;
              button.disabled = false;
              button.outlined = true;
              button.callback = this.showRevision;
              buttons.push(button);
            }

            if (this.lastContrat.toApprove) {
              {
                const button = new Button();
                button.show = true;
                button.callback = this.fermerContrat;
                button.label = RessourceFwkButton(FwkButtons.PAS_ACCEPTER).label;
                button.disabled = false;
                button.outlined = true;
                buttons.push(button);
              }
              {
                const button = new Button();
                button.show = true;
                button.callback = this.approveContrat;
                button.label = this.signClicked && !this.signing ? 'Vérifier le statut de signature' : RessourceFwkButton(FwkButtons.SIGNER_CONTRAT).label;
                button.disabled = this.signing;
                button.loading = this.buttonLoading['sign']?.loading || false;
                buttons.push(button);
              }
            } else if (this.lastContrat.statut == ContratStatuts.APPROUVED) {
              const button = new Button();
              button.show = true;
              button.callback = this.getSignLink;
              button.label = this.signClicked && !this.signing ? 'Vérifier le statut de signature' : RessourceFwkButton(FwkButtons.SIGNER_CONTRAT).label;
              button.disabled = this.signing;
              button.loading = this.buttonLoading['sign']?.loading || false;
              buttons.push(button);
            } else {
              const button = new Button();
              button.show = true;
              button.callback = this.fermerContrat;
              button.label = RessourceFwkButton(FwkButtons.FERMER).label;
              button.disabled = false;
              button.outlined = false;
              buttons.push(button);
            }
          }
        }

        if (this.lastContrat.statut == ContratStatuts.SIGNED) {
          {
            const button = new Button();
            button.show = true;
            button.label = RessourceFwkButton(FwkButtons.DOWNLOAD_DOCUMENT).label;
            button.callback = this.DownloadDocument;
            button.disabled = false;
            buttons.push(button);
          }
        }
        return buttons;
      }
    },
    methods: {
      DownloadDocument(): Promise<void> {
        return new Promise((resolve) => {
          let fileName = this.document.fileName;
          const data = new DownloadData();
          data.service = 'DOWNLOAD';
          data.type.extension = this.document.fileExtension;
          data.data = JSON.stringify(this.document);
          Files.download(data)
            .then((buffer) => {
              const blob = getBlob(buffer, data.type.extension);
              const file = window.URL.createObjectURL(blob);
              const docUrl = document.createElement('a');
              if (!fileName.endsWith(data.type.extension)) {
                fileName = `${fileName}.${data.type.extension}`
              }
              docUrl.setAttribute('href', file);
              docUrl.setAttribute('download', fileName);
              document.body.appendChild(docUrl);
              docUrl.click();
            });
          resolve();
        });
      },
      setDocument() {
        const document = storeDocument.listDocument.find(a => a.objType == ObjTypes.CONTRAT && a.objId == this.lastContrat.id);
        if (document) {
          this.document = document;
        } else {
          const objType = new ObjType()
          objType.type = ObjTypes.CONTRAT;
          objType.id = this.lastContrat.id;
          Document.getByObjType(objType).then((doc) => {
            this.document = doc;
          });
        }
      },
      FermerDialog(): Promise<void> {
        return new Promise((resolve) => {
          this.closeDialog();
          resolve();
        });
      },
      validateContrat(): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          Contrat.submit(this.lastContrat).then(() => {
            resolve();
          });
        });
      },
      async approveContrat(): Promise<void> {
        if (this.buttonLoading['sign']?.loading) return;
        this.approving = true;
        this.buttonLoading['sign'] = { loading: true };
        this.subSection = new Section();
        try {
          const result = await Contrat.approve(this.lastContrat);
          if (!this.disposed) this.lastContrat.fromContrat(result);
        } catch {
          this.approving = false;
          this.signError = 'Impossible de préparer la signature. Veuillez réessayer.';
        } finally {
          this.buttonLoading['sign'] = { loading: false };
          if (this.approving && this.lastContrat.statut === ContratStatuts.APPROUVED) {
            this.approving = false;
            await this.getSignLink();
          }
        }
      },
      async getSignLink(): Promise<void> {
        if (this.buttonLoading['sign']?.loading || this.signing || this.disposed) return;
        this.buttonLoading['sign'] = { loading: true };
        this.signError = '';
        this.subSection = new Section();
        try {
          const link = await EduSign.signatureLinkWithReconciliation(this.document.id);
          const contrat = await Contrat.get(this.lastContrat.id);
          if (this.disposed) return;
          this.lastContrat.fromContrat(contrat);
          if (!this.signClicked && link && !this.signatureComplete) this.signContrat(link);
          else if (!this.signatureComplete) {
            this.signClicked = true;
            this.signError = 'La signature n’est pas encore confirmée. Veuillez vérifier à nouveau son statut.';
          }
        } catch {
          this.signError = 'Impossible de vérifier la signature. Veuillez réessayer.';
        } finally {
          this.buttonLoading['sign'] = { loading: false };
        }
      },
      signRepresentant(): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          Contrat.sign(this.lastContrat).then(() => {
            resolve();
          });
        });
      },
      signContrat(url: string): void {
        if (!url || this.disposed || this.signatureComplete || (!this.popupBlocked && Date.now() - this.lastOpenAt < 2000)) return;
        this.lastOpenAt = Date.now();
        this.signUrl = url.trim();
        this.signClicked = true;
        this.signing = true;
        sessionStorage.setItem('edusignSigningInProgress', JSON.stringify({
          documentId: this.document.id,
          expiresAt: Date.now() + 30 * 60 * 1000
        }));
        this.signWindow = window.open(encodeURI(this.signUrl), '_blank');
        this.popupBlocked = !this.signWindow;
        window.clearInterval(this.signTimer);
        if (this.signWindow) {
          this.signTimer = window.setInterval(() => {
            if (this.signWindow?.closed) this.signatureReturn();
            try {
              if (this.signWindow?.location.origin === window.location.origin &&
                  this.signWindow.location.pathname === '/signed') this.signatureReturn();
            } catch {
              // Edusign is on another origin until the return page is reached.
            }
          }, 500);
        }
      },
      signatureReturn(): void {
        if (!this.signWindow) return;
        this.signing = false;
        window.clearInterval(this.signTimer);
      },
      commentContrat(): Promise<void> {
        return new Promise((resolve) => {
          this.showChat = true;
          resolve();
        });
      },
      modifyContrat(): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          Contrat.modify(this.lastContrat).then(() => {
            resolve();
          });
        });
      },
      fermerContrat(): Promise<void> {
        return new Promise((resolve) => {
          this.closeDialog()
          resolve();
        });
      },
      showArticles(): Promise<void> {
        return new Promise((resolve) => {
          ContratArticle.canEdit(this.lastContrat).then((res: boolean) => {
            if (res && res.failed == undefined) {
              this.subSection.component = SectionEditArticles;
              this.subSection.type = 'contratTemplateId';
              this.subSection.data = this.lastContrat.contratTemplateId;
              this.subSection.propType = 'contratId';
              this.subSection.propData = this.lastContrat.id;
              this.subSection.fullscreen = true;
            }
            resolve();
          }).catch(() => {
            resolve();
          });
        });
      },
      showPeriodes(): Promise<void> {
        return new Promise((resolve) => {
          const editCreneaux: Creneau[] = [];
          this.subSection.component = CreneauxEditor;
          this.subSection.type = 'contrat';
          this.subSection.data = this.lastContrat;
          this.subSection.propType = 'creneaux';
          this.subSection.propData = editCreneaux;
          resolve();
        });
      },
      showRemuneration(): Promise<void> {
        return new Promise((resolve) => {
          this.subSection.component = SectionRemuneration;
          this.subSection.type = 'contrat';
          this.subSection.data = this.lastContrat;
          resolve();
        });
      },


      filterMetaAttrib(metaAttrib: MetaAttrib[]): MetaAttrib[] {
        const filterMetaAttrib = metaAttrib.filter(a => {
          return (
            a.classe == 'CONTRAT' &&
            (a.contratTemplateId == this.lastContrat.contratTemplateId) &&
            (a.conventionIds.includes(this.lastContrat.conventionAppliqueeId) || a.conventionIds.length == 0) &&
            (a.emploiIds.includes(this.lastContrat.emploiId) || a.emploiIds.length == 0)
          )
        });
        filterMetaAttrib.forEach((meta: MetaAttrib) => {
          meta.docObjType = ObjTypes.CONTRAT;
          meta.docObjId = this.lastContrat.id;
        });
        return filterMetaAttrib;
      },


      showObjet(): void {
        this.subSection.component = SectionValuateMetaAttrib;
        this.subSection.type = 'metaAttribs';
        this.subSection.showButton = true;
        this.subSection.data = this.metaAttrib;
      },
      showRevision(): Promise<void> {
        return new Promise((resolve) => {
          this.subSection = new Section();
          this.showModif = !this.showModif;
          this.document.showModif = this.showModif;
          resolve();
        });
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      closeDialog(): void {
        this.$emit('closeDialog');
      },
      closeSubDialog(): void {
        this.showChat = false;
        this.subSection = new Section();
      }
    }
  })

</script>

<style scoped>
  .contract-signature-status {
    display: flex;
    min-height: 44px;
    padding: 0.5rem 0.25rem 0.5rem 1rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-style: italic;
    text-align: center;
  }

  .contract-signature-status p {
    margin-bottom: 0 !important;
  }

  .contract-signature-status button {
    font: inherit;
  }
</style>
